import React, { useMemo } from "react"

import { PayerCoverageCheckNetwork } from "@common-shared/config/coverage.config.ts"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import {
    IOVCoverageResultDemographics,
    IOneVerifyCoverageCheckHistory,
} from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { CHECKER_CONFIG } from "../../../config/checker.config"
import { PoliciesHelper } from "../../../helpers/policies.helper"
import { useAppDispatch } from "../../../store/appDispatch.hook.ts"
import { useAppSelector } from "../../../store/selectors/app.selector.ts"
import { checkerSelector } from "../../../store/selectors/checker.selector.ts"
import {
    checkerReceivedContinuousMonitoringCoverageResult,
    checkerReceivedMedicaidCoverageResult,
} from "../../../store/slices/checker.slice.ts"
import type { ICommonCheckResult } from "../../../types/objects.types.ts"
import { WriteToEhrButtonComponent } from "../../policies/writeToEhr/writeToEhrButton.component.tsx"
import { CoverageResultBreakdownWrapperComponent } from "./coverageResultBreakdownWrapper.component"
import { CoverageResultGeneralComponent } from "./coverageResultGeneral.component"

export const CoverageResultComponent: React.FunctionComponent<
    ICoverageResultComponentProps
> = props => {
    const dispatch = useAppDispatch()
    const { manualCheckType } = useAppSelector(checkerSelector)

    const selectedResult =
        useMemo<IOneVerifyCoverageCheckHistory | null>(() => {
            const inNetworkResult = props.results[PayerCoverageCheckNetwork.IN]
            const outNetworkResult =
                props.results[PayerCoverageCheckNetwork.OUT]

            return inNetworkResult?.coverageResult.resultType &&
                CHECKER_CONFIG.activeCoverageResults.includes(
                    inNetworkResult?.coverageResult.resultType,
                )
                ? inNetworkResult
                : outNetworkResult?.coverageResult.resultType &&
                    CHECKER_CONFIG.activeCoverageResults.includes(
                        outNetworkResult?.coverageResult.resultType,
                    )
                  ? outNetworkResult
                  : inNetworkResult || outNetworkResult || null
        }, [props.results])

    // Difference between demographics that was provided by user and that insurer has on file
    const demographicsDifference = useMemo<
        Partial<IOVCoverageResultDemographics>
    >(() => {
        if (!selectedResult) {
            return {}
        }

        return PoliciesHelper.getDemographicsDifference(
            selectedResult?.requestData,
            selectedResult?.coverageResult.demographics,
        )
    }, [selectedResult])

    const isInputOverridden = useMemo<boolean>(() => {
        return (
            !!Object.keys(demographicsDifference).length ||
            (!!selectedResult?.responsePayer &&
                !!selectedResult.requestPayer &&
                selectedResult.responsePayer.payerId !==
                    selectedResult.requestPayer.payerId)
        )
    }, [demographicsDifference, selectedResult])

    return (
        <div className={`relative ${props.className}`}>
            {props.results && (
                <div
                    className="
                        max-w-[1200px] mx-auto
                        md:max-w-none md:w-full
                        flex justify-end
                    "
                >
                    <WriteToEhrButtonComponent
                        className="mb-[24px]"
                        coverageCheckResult={props.results}
                        onSuccess={result => {
                            manualCheckType === QuotaUsageType.MEDICAID
                                ? dispatch(
                                      checkerReceivedMedicaidCoverageResult(
                                          result,
                                      ),
                                  )
                                : dispatch(
                                      checkerReceivedContinuousMonitoringCoverageResult(
                                          result,
                                      ),
                                  )
                        }}
                    />
                </div>
            )}

            <CoverageResultGeneralComponent
                className="
                    max-w-[1200px] mx-auto
                    md:max-w-none md:w-full
                "
                rounded
                isDefaultCollapsed={props.isDefaultCollapsed}
                isInputOverridden={isInputOverridden}
                isSmartScanResult={
                    selectedResult?.coverageResult.isSmartScanResult &&
                    CHECKER_CONFIG.activeCoverageResults.includes(
                        selectedResult.coverageResult.resultType,
                    )
                }
                coverageCheckHistory={selectedResult}
            />

            {/*RESULTS BREAKDOWN*/}

            {props.results[PayerCoverageCheckNetwork.IN] ? (
                <CoverageResultBreakdownWrapperComponent
                    className="
                        max-w-[1200px] mx-auto
                        md:max-w-none md:w-full
                    "
                    requestData={
                        props.results[PayerCoverageCheckNetwork.IN].requestData
                    }
                    coverageResult={
                        props.results[PayerCoverageCheckNetwork.IN]
                            .coverageResult
                    }
                    requestPayer={
                        props.results[PayerCoverageCheckNetwork.IN]
                            .requestPayer ||
                        props.results[PayerCoverageCheckNetwork.IN]
                            .responsePayer
                    }
                    responsePayer={
                        props.results[PayerCoverageCheckNetwork.IN]
                            .responsePayer
                    }
                    requestedHealthProvider={
                        props.results[PayerCoverageCheckNetwork.IN]
                            .requestedHealthProvider
                    }
                    policy={props.results[PayerCoverageCheckNetwork.IN].policy}
                    agenticCheck={
                        props.results[PayerCoverageCheckNetwork.IN].agenticCheck
                    }
                    preAgenticCoverageResult={
                        props.results[PayerCoverageCheckNetwork.IN]
                            .preAgenticCoverageResult
                    }
                    withFlagsSection={true}
                    isSmartScanFloating={false}
                />
            ) : null}

            {props.results[PayerCoverageCheckNetwork.OUT] ? (
                <CoverageResultBreakdownWrapperComponent
                    className="
                        max-w-[1200px] mx-auto
                        md:max-w-none md:w-full
                    "
                    requestData={
                        props.results[PayerCoverageCheckNetwork.OUT].requestData
                    }
                    coverageResult={
                        props.results[PayerCoverageCheckNetwork.OUT]
                            .coverageResult
                    }
                    requestPayer={
                        props.results[PayerCoverageCheckNetwork.OUT]
                            .requestPayer ||
                        props.results[PayerCoverageCheckNetwork.OUT]
                            .responsePayer
                    }
                    responsePayer={
                        props.results[PayerCoverageCheckNetwork.OUT]
                            .responsePayer
                    }
                    requestedHealthProvider={
                        props.results[PayerCoverageCheckNetwork.OUT]
                            .requestedHealthProvider
                    }
                    policy={props.results[PayerCoverageCheckNetwork.OUT].policy}
                    agenticCheck={
                        props.results[PayerCoverageCheckNetwork.OUT]
                            .agenticCheck
                    }
                    preAgenticCoverageResult={
                        props.results[PayerCoverageCheckNetwork.OUT]
                            .preAgenticCoverageResult
                    }
                    withFlagsSection={true}
                    isSmartScanFloating={false}
                />
            ) : null}
        </div>
    )
}

interface ICoverageResultComponentProps {
    className?: string
    isDefaultCollapsed?: boolean
    results: ICommonCheckResult
}
